import { useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO, startOfDay } from "date-fns";
import { CalendarPlus, CalendarDays, Loader2, Lock, Save, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

const SLEEP_OPTIONS = [
  { slider: 1, ms: 900000, label: "15 min" },
  { slider: 2, ms: 1800000, label: "30 min" },
  { slider: 3, ms: 3600000, label: "1 hour" },
  { slider: 4, ms: 7200000, label: "2 hours" },
];

const sliderToMs = (s: number) => SLEEP_OPTIONS.find((o) => o.slider === s)?.ms ?? 3600000;
const msToSlider = (ms: number) => {
  if (!ms) return 3;
  // Snap to closest option
  let best = SLEEP_OPTIONS[0];
  let bestDiff = Math.abs(ms - best.ms);
  for (const o of SLEEP_OPTIONS) {
    const d = Math.abs(ms - o.ms);
    if (d < bestDiff) { best = o; bestDiff = d; }
  }
  return best.slider;
};
const msLabel = (ms: number) => SLEEP_OPTIONS.find((o) => o.ms === ms)?.label ?? `${Math.round(ms / 60000)} min`;

const DAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const parseDatesCsv = (csv: string | null | undefined): string[] => {
  if (!csv || typeof csv !== "string") return [];
  const seen = new Set<string>();
  for (const part of csv.split(",")) {
    const t = part.trim();
    if (!t) continue;
    const d = parseISO(t);
    if (isValid(d)) seen.add(format(d, "yyyy-MM-dd"));
  }
  return Array.from(seen).sort();
};

interface Props {
  device: any;
  deviceId: string;
  onUpdate: () => Promise<void> | void;
}

export const EventScheduleCard = ({ device, deviceId, onUpdate }: Props) => {
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { logChange } = useDeviceChangeLog();

  const initialCsv: string = device?.deviceEventDates ?? "";
  const initialSleep: number =
    Number(device?.eventScheduleSleepTime) ||
    Number(device?.camera_config?.eventScheduleSleepTime) ||
    3600000;

  const [dates, setDates] = useState<string[]>(() => parseDatesCsv(initialCsv));
  const [sleepSlider, setSleepSlider] = useState<number>(msToSlider(initialSleep));
  const [savedSleepMs, setSavedSleepMs] = useState<number>(sliderToMs(msToSlider(initialSleep)));
  const [isSaving, setIsSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    setDates(parseDatesCsv(device?.deviceEventDates ?? ""));
    const ms =
      Number(device?.eventScheduleSleepTime) ||
      Number(device?.camera_config?.eventScheduleSleepTime) ||
      3600000;
    const slider = msToSlider(ms);
    setSleepSlider(slider);
    setSavedSleepMs(sliderToMs(slider));
  }, [device?.deviceEventDates, device?.eventScheduleSleepTime, device?.camera_config?.eventScheduleSleepTime]);

  const operationalDays = useMemo<Set<string>>(() => {
    const csv: string = device?.deviceOperationalDays ?? "Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday";
    const set = new Set<string>();
    for (const part of csv.split(",")) {
      const t = part.trim();
      if (t) set.add(t.toLowerCase());
    }
    return set;
  }, [device?.deviceOperationalDays]);

  const today = startOfDay(new Date());

  const invalidUpcomingDates = useMemo(() => {
    return dates.filter((iso) => {
      const d = parseISO(iso);
      if (!isValid(d)) return false;
      if (startOfDay(d) < today) return false; // ignore past
      const weekday = DAYS_LONG[d.getDay()];
      return !operationalDays.has(weekday.toLowerCase());
    });
  }, [dates, operationalDays, today]);

  const hasWarning = invalidUpcomingDates.length > 0;

  const persistDates = async (next: string[]) => {
    const csv = next.join(",");
    setIsSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "deviceEventDates");
      params.append("value", csv);
      logChange(deviceId, "deviceEventDates", csv);
      const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!res.ok) throw new Error("BuildShip update failed");

      const { error } = await supabase
        .from("device_details")
        .update({ device_event_dates: csv })
        .eq("device_id", deviceId);
      if (error) throw error;

      await onUpdate?.();
      toast.success("Event dates updated");
    } catch (err) {
      console.error("Failed to save event dates:", err);
      toast.error("Failed to update event dates");
      // Rollback local state on failure
      setDates(parseDatesCsv(device?.deviceEventDates ?? ""));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDate = (d: Date | undefined) => {
    if (!d) return;
    const iso = format(d, "yyyy-MM-dd");
    if (dates.includes(iso)) {
      setPickerOpen(false);
      return;
    }
    const next = [...dates, iso].sort();
    setDates(next);
    setPickerOpen(false);
    persistDates(next);
  };

  const handleRemoveDate = (iso: string) => {
    const next = dates.filter((d) => d !== iso);
    setDates(next);
    persistDates(next);
  };

  const handleSaveSleep = async () => {
    const ms = sliderToMs(sleepSlider);
    setIsSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", "cameraConfig.eventScheduleSleepTime");
      params.append("value", String(ms));
      logChange(deviceId, "cameraConfig.eventScheduleSleepTime", String(ms));
      const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
      if (!res.ok) throw new Error("BuildShip update failed");

      const { error } = await supabase
        .from("device_details")
        .update({ event_schedule_sleep_time: ms })
        .eq("device_id", deviceId);
      if (error) throw error;

      setSavedSleepMs(ms);
      await onUpdate?.();
      toast.success("Event sleep schedule updated");
    } catch (err) {
      console.error("Failed to save event sleep time:", err);
      toast.error("Failed to update event sleep schedule");
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = !canEditDeviceConfig || isSaving;
  const sleepDirty = sliderToMs(sleepSlider) !== savedSleepMs;

  return (
    <Card
      className={cn(
        "border bg-muted/50",
        hasWarning && "border-destructive bg-destructive/5"
      )}
    >
      <CardHeader className="py-3">
        <div className="flex items-center gap-2">
          <CalendarDays className={cn("h-4 w-4", hasWarning ? "text-destructive" : "text-brand-500")} />
          <CardTitle className="text-base">Event Schedule</CardTitle>
          {!canEditDeviceConfig && <Lock className="h-3 w-3 text-muted-foreground ml-auto" />}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {/* Dates */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Event Dates</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  className="h-7 text-xs"
                >
                  <CalendarPlus className="h-3 w-3 mr-1" />
                  Add Date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  onSelect={handleAddDate}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {dates.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              No event dates. Camera uses the normal interval timer every day.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {dates.map((iso) => {
                const d = parseISO(iso);
                const isPast = startOfDay(d) < today;
                const weekday = DAYS_LONG[d.getDay()];
                const isInvalid = !isPast && !operationalDays.has(weekday.toLowerCase());
                return (
                  <Badge
                    key={iso}
                    variant={isInvalid ? "destructive" : isPast ? "outline" : "secondary"}
                    className="gap-1 pr-1"
                  >
                    <span className="text-[11px]">{format(d, "dd MMM yyyy")}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDate(iso)}
                      disabled={disabled}
                      className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 p-0.5 disabled:opacity-50"
                      aria-label={`Remove ${iso}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
        </div>

        {/* Warning */}
        {hasWarning && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              Camera is not operational on the following event day{invalidUpcomingDates.length > 1 ? "s" : ""}:
              <ul className="mt-1 list-disc list-inside">
                {invalidUpcomingDates.map((iso) => {
                  const d = parseISO(iso);
                  return (
                    <li key={iso}>
                      {format(d, "dd MMM yyyy")} ({DAYS_LONG[d.getDay()]})
                    </li>
                  );
                })}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Sleep time slider */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="eventSleepTime" className="text-sm">Event Day Interval</Label>
            <span className="text-xs font-medium">{msLabel(sliderToMs(sleepSlider))}</span>
          </div>
          <div className="flex gap-2 items-center">
            <Slider
              id="eventSleepTime"
              min={1}
              max={4}
              step={1}
              value={[sleepSlider]}
              onValueChange={(v) => setSleepSlider(v[0])}
              disabled={disabled}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveSleep}
              disabled={disabled || !sleepDirty}
              className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground"
            >
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            On event dates, the camera uses this interval instead of the normal one.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};